import React from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import {Card, CardBody, CardTitle} from 'reactstrap';
import styled from 'styled-components';

import {ClickToEditButton} from 'app/components/general/ClickToEditButton';
import {RichTextarea} from 'app/components/general/RichTextarea';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import StateButton from 'app/general/widgets/StateButton';
import {ProcedureStepNumber} from 'app/procedures/components/ProcedureStepNumber';
import {useAccountSettingsState} from 'app/settings/api';
import {getObjectName} from 'app/utils/fallbacks';
import {gt, ngt} from 'app/utils/i18n';
import {cdnImageSrc, countWords, numberToLetter} from 'app/utils/strings';

import {Countdown} from './Countdown';

import type {
  AnswerType,
  AttemptSerializedType,
  QuestionType,
} from 'sweet-types';

type Props = {
  index: number;
  question: QuestionType;
  duration?: number;
  isEditable?: boolean;
  attempt?: AttemptSerializedType;
  onAnswer?: (props: {
    selectedAnswerId: string;
    question: QuestionType;
  }) => unknown;
  onFreeform?: (props: {freeform: string; question: QuestionType}) => unknown;
  freeform?: string;
  setFreeform?: (value) => void;
  selectedAnswers: Array<string>;
  correctAnswers: Record<string, boolean>;
  children?: React.ReactNode;
};

export const SingleQuestionContent = ({
  index,
  duration,
  question,
  isEditable,
  attempt,
  onAnswer,
  onFreeform,
  freeform,
  setFreeform,
  selectedAnswers,
  correctAnswers,
  children,
}: Props) => {
  const {MEDIA_URL, MEDIA_URL_READ} = useAccountSettingsState().data!;
  const isAnswered =
    attempt != null &&
    attempt.answers.some((answer) => answer.question_id === question.id);

  const correctAnswer: AnswerType | undefined = question.answers.find(
    (answer) => correctAnswers && correctAnswers[answer.id] === true
  );

  return (
    <Card>
      <CardBody>
        <CardTitle className="mb-4">
          <div className="mb-2 d-flex align-items-start justify-content-between">
            <div className="d-flex align-items-center">
              <StyledBigText>
                <ProcedureStepNumber n={index + 1} type={'content'} />
              </StyledBigText>{' '}
              {attempt != null ? (
                <small className="text-muted ml-1 font-italic">
                  {gt('of %s', attempt.questions.length)}
                </small>
              ) : null}
            </div>
            {onAnswer && attempt != null ? (
              <Countdown attempt={attempt} duration={duration} />
            ) : null}
          </div>
          <h4>
            {getObjectName(question.text, 'question', true)}
            {isEditable && <ClickToEditButton />}
          </h4>
        </CardTitle>
        {question.description && (
          <UserContentContainer
            mediaURL={MEDIA_URL}
            mediaURLRead={MEDIA_URL_READ}
            content={question.description}
          />
        )}
        <div className="list-group mt-3">
          {question.type == 'freeform' && setFreeform ? (
            <FreeformAnswer
              key={question.id}
              freeform={freeform ?? ''}
              setFreeform={setFreeform}
              question={question}
              isAnswered={isAnswered}
              onFreeform={onFreeform}
            />
          ) : (
            question.answers.map((answer, answerIndex) => (
              <Answer
                key={answer.id}
                attempt={attempt}
                answer={answer}
                question={question}
                answerIndex={answerIndex}
                correctAnswerId={correctAnswer?.id}
                selectedAnswers={selectedAnswers}
                onAnswer={onAnswer}
              />
            ))
          )}
        </div>
        <Explanation
          question={question}
          isAnswered={isAnswered}
          correctAnswer={correctAnswer}
        />
      </CardBody>
      {children}
    </Card>
  );
};

const StyledBigText = styled.div.attrs({className: 'mb-1'})`
  font-size: 1.4em;
`;

type AnswerProps = {
  question: QuestionType;
  answer: AnswerType;
  answerIndex: number;
  attempt?: AttemptSerializedType;
  onAnswer?: (props: {
    selectedAnswerId: string;
    question: QuestionType;
  }) => unknown;
  selectedAnswers: Array<string>;
  correctAnswerId?: string;
};

const Answer = ({
  question,
  answer,
  attempt,
  answerIndex,
  onAnswer,
  selectedAnswers,
  correctAnswerId,
}: AnswerProps) => {
  const isSelected = selectedAnswers.includes(answer.id);
  const isViewingAttempt = onAnswer == null && attempt?.status == 'completed';
  const isAnswerChosen = selectedAnswers.length > 0;
  const isAllowedToSeeAnswer = isAnswerChosen || isViewingAttempt;
  const isCorrectAnswer = isAllowedToSeeAnswer && correctAnswerId === answer.id;

  return (
    <button
      className={cs(
        'list-group-item list-group-item-action d-flex p-2 justify-content-between align-items-center',
        {
          'list-group-item-success': isSelected && isCorrectAnswer,
          'list-group-item-danger': isSelected && !isCorrectAnswer,
        }
      )}
      key={answer.id}
      onClick={(e) => {
        e.preventDefault();
        onAnswer && onAnswer({selectedAnswerId: answer.id, question});
      }}
    >
      <div className="d-flex w-100">
        <div>
          <div className="bg-light rounded px-2 text-muted mr-2">
            {numberToLetter(answerIndex)}
          </div>
        </div>
        {getObjectName(answer.text, 'answer', true)}
      </div>
      {isCorrectAnswer ? (
        <div>
          <span className="badge mt-1 ml-2 float-right badge-success">
            <span className="d-none d-md-inline mr-1">{gt('Correct')}</span>
            <i className="fa fa-check" />
          </span>
        </div>
      ) : null}
    </button>
  );
};

type FreeformAnswerProps = {
  question: QuestionType;
  freeform: string;
  isAnswered: boolean;
  setFreeform: (value) => void;
  onFreeform?: (props: {freeform: string; question: QuestionType}) => unknown;
};

const FreeformAnswer = ({
  question,
  freeform,
  setFreeform,
  isAnswered,
  onFreeform,
}: FreeformAnswerProps) => {
  const {MEDIA_URL, MEDIA_URL_READ} = useAccountSettingsState().data!;
  return (
    <>
      {isAnswered && (
        <div className="mt-3 pt-2 border-top">
          <UserContentContainer
            mediaURL={MEDIA_URL}
            mediaURLRead={MEDIA_URL_READ}
            content={freeform}
          />
          <StyledWordCountDiv>
            {ngt('%d Word', '%d Words', countWords(freeform))}
          </StyledWordCountDiv>
        </div>
      )}
      {onFreeform && !isAnswered && (
        <>
          <RichTextarea
            value={cdnImageSrc(freeform, MEDIA_URL_READ, 'original')}
            contentType="quiz"
            objectId={question.id}
            options={{
              placeholderText: gt(
                'Please provide a detailed response to the question. Your answer should be well-thought-out and articulate.'
              ),
              heightMin: 140,
              wordCounterCount: true,
            }}
            onChange={setFreeform}
            onCloseRequest={() => undefined}
          />
          <div>
            <StateButton
              className="btn-primary"
              busyText="loading..."
              disabled={!freeform}
              onClick={async () => {
                await onFreeform({freeform, question});
              }}
            >
              {gt('Submit')}
            </StateButton>
            <small className="text-muted ml-3 d-sm-inline d-none">
              {gt('Once your answer is submitted, it cannot be changed.')}
            </small>
          </div>
        </>
      )}
    </>
  );
};

const StyledWordCountDiv = styled.div.attrs({
  className: 'text-right',
})`
  color: ${colors.lightGray};
  transition: color 0.3s ease;
  &:hover {
    color: ${colors.mediumGray};
  }
`;

type ExplanationType = {
  question: QuestionType;
  isAnswered: boolean;
  correctAnswer?: AnswerType;
};

const Explanation = ({
  question,
  isAnswered,
  correctAnswer,
}: ExplanationType) => {
  const {MEDIA_URL, MEDIA_URL_READ} = useAccountSettingsState().data!;
  return (
    <>
      {question.explanation && isAnswered && (
        <div className="mt-2 border rounded p-3">
          {correctAnswer && (
            <div className="alert alert-info">
              <UserContentContainer
                mediaURL={MEDIA_URL}
                mediaURLRead={MEDIA_URL_READ}
                content={correctAnswer.text}
              />
            </div>
          )}
          <UserContentContainer
            mediaURL={MEDIA_URL}
            mediaURLRead={MEDIA_URL_READ}
            content={question.explanation}
          />
        </div>
      )}
    </>
  );
};
